import { useMemo } from 'react';
import { useAppContext } from '../../context/AppContext';
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Tooltip,
    Legend,
} from 'recharts';
import { getProductPrice } from '../../utils/pricing';

const COLORS = ['#0d6efd', '#198754', '#ffc107', '#dc3545', '#6610f2', '#fd7e14', '#20c997', '#d63384'];

export default function ProductDistributionChart() {
    const { products, sales } = useAppContext();

    const data = useMemo(() => {
        if (!products.length || !sales.length) return [];

        const map = new Map();

        sales.forEach((s) => {
            const product = products.find((p) => p.id === s.productId);
            if (!product) return;

            const price = getProductPrice(product);
            const revenue = price * s.quantity;

            const current = map.get(product.id) || {
                name: product.name,
                value: 0,
                quantity: 0,
            };
            current.value += revenue;
            current.quantity += s.quantity;
            map.set(product.id, current);
        });

        return Array.from(map.values())
            .sort((a, b) => b.value - a.value)
            .slice(0, 8); // Top 8 produtos
    }, [products, sales]);

    if (!data.length) {
        return (
            <div className="card shadow-sm mb-4">
                <div className="card-body">
                    <h2 className="h6 mb-2">Distribuição de Receita por Produto</h2>
                    <p className="text-muted small mb-0">
                        Lance vendas para visualizar a distribuição.
                    </p>
                </div>
            </div>
        );
    }

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            const data = payload[0].payload;
            return (
                <div className="bg-white border rounded p-2 shadow-sm" style={{ fontSize: '0.875rem' }}>
                    <div className="fw-bold">{data.name}</div>
                    <div className="text-muted">Receita: R$ {data.value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</div>
                    <div className="text-muted">Qtd vendida: {data.quantity}</div>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="card shadow-sm mb-4">
            <div className="card-body">
                <h2 className="h6 mb-3">Distribuição de Receita por Produto</h2>
                <div style={{ width: '100%', height: 300 }}>
                    <ResponsiveContainer>
                        <PieChart>
                            <Pie
                                data={data}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="value"
                            >
                                {data.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                            <Legend />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}
